import { useState } from 'react';
import { useT } from '@/store';
import { useCreateOrder } from './useOrders';

/**
 * Form state and validation for creating an order, shared by the
 * dispatcher's QuickDispatchModal and the Orders tab's CreateOrderDrawer.
 * The monolith had this logic twice and the copies had already drifted.
 *
 * Leaving the driver unset is meaningful, not an oversight: the backend
 * auto-assigns the nearest available driver when driver_id is omitted,
 * which is the normal path for a dispatcher who does not care which truck
 * takes the job. Note "nearest" literally — core/auto_dispatch.py ranks by
 * straight-line distance alone, which is NOT what the recommended-driver
 * list beside this form ranks by; OrderForm.jsx says so on screen.
 */
const EMPTY = {
  driver_id: '',
  quarry_id: '',
  site_id: '',
  // 'existing' picks a client_sites row from the dropdown, same as always.
  // 'new' is a one-off delivery address (search or map pin) that becomes a
  // lightweight client_sites row at submit time — see
  // blueprints/orders.py's _resolve_or_create_site_id() and this feature's
  // plan for why that still ends in a real site rather than a schema
  // change. Mirrors ClientOrderForm.jsx's locationMode toggle.
  location_mode: 'existing',
  new_site_name: '',
  new_client_name: '',
  new_site_pin: null,
  new_site_radius_m: '150',
  cargo_type: '',
  estimated_weight_tons: '',
  // Kiek mašinų šito darbo. '1' kaip tekstas, ne 1 kaip skaicius: laukas yra
  // <input>, ir jo reiksme visada grizta eilute — dvi skirtingos formos tam
  // paciam laukui butu pirma vieta, kur reset() ir set() nesutaptu.
  loads: '1',
  // Kada sitas krovinys pazadetas. '' reiskia "dabar" — toks yra kiekvienas
  // uzsakymas, suvestas iki sio lauko, ir marsrutas be jo elgiasi taip pat.
  scheduled_for: '',
  agreed_price_per_ton: '',
  // V-352. The HOURS inside that promised day, when a dispatcher agrees them
  // on the phone. The DAY is scheduled_for above — one field, not two.
  //
  // Until this existed only the B2B portal could promise a time, so the
  // on-time share (core/on_time.py) could only ever speak for the minority
  // of loads a client booked themselves. Both blank by default: a load
  // nobody promised an hour for is the ordinary case and behaves exactly as
  // it did.
  window_start: '',
  window_end: '',
  note: '',
  // Transporto kainodaros modeliai: optional at creation, same as
  // agreed_price_per_ton above — a dispatcher who does not yet know the
  // rate leaves this at 'per_ton' and sets the real method later from the
  // order's own card. See pricingFields() below for why only the active
  // method's own fields are ever sent.
  pricing_method: 'per_ton',
  distance_km_one_way: '',
  is_round_trip: false,
  price_per_km: '',
  flat_price_eur: '',
  hourly_rate_eur: '',
  billed_hours: '',
  tonkm_capacity_tons: '',
};

/**
 * A rate off a phone call, or undefined.
 *
 * '' -> undefined, NOT 0. Number('') is 0 in JavaScript, and a 0 reaching
 * orders.agreed_price_per_ton does not mean "no rate agreed" — the invoice
 * COALESCEs it over the price list and bills the load at nothing. The
 * backend refuses it outright (err.field_positive_number); this makes sure
 * the blank field never asks it to. The Lithuanian decimal comma is taken
 * because that is what the keyboard and the price list both use.
 */
function agreedRate(raw) {
  const text = String(raw ?? '').trim().replace(',', '.');
  if (text === '') return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Transporto kainodaros modeliai: the create-time payload for whichever
 * pricing_method is selected, or {} for 'per_ton' — the ordinary case,
 * where agreed_price_per_ton (or the price list) decides and none of these
 * fields apply. Only the ACTIVE method's own fields are ever sent, mirroring
 * POST .../pricing-method's own "every other method's field is NULL" rule
 * — a stray price_per_km on a 'flat' order would be exactly the kind of
 * stale field that rule exists to prevent.
 */
/** The three promised-window fields, or nothing at all.
 *
 *  The half-open case is deliberately NOT caught here. The server refuses
 *  it (err.time_window_incomplete) and so does the portal, and a second
 *  copy of that rule in the browser is the copy that drifts — the same
 *  reasoning core/delivery_window.py's docstring gives for owning the shape
 *  in one place. What this does is only the shape of the request: send the
 *  three fields when the dispatcher typed anything, send none when they
 *  did not. */
// Named ...Fields, not deliveryWindow: frontend/src/lib/deliveryWindow.js
// exports a deliveryWindow() that answers the opposite question — whether a
// SAVED order is about to miss the promise it already carries. This one
// builds the promise on the way in. Two jobs, and a shared name is how a
// later import quietly shadows one with the other.
function deliveryWindowFields(values) {
  const start = values.window_start?.trim();
  const end = values.window_end?.trim();
  // Keys are OMITTED rather than set to undefined. JSON.stringify would
  // strip them either way, so the wire request is the same — but a caller
  // that ever inspects the object (a test, a log, a future retry queue)
  // sees "no promise" instead of "a promise of undefined", and those are
  // different statements about the same load.
  const out = {};
  if (start) out.window_start = start;
  if (end) out.window_end = end;
  return out;
}

function pricingFields(values) {
  const num = agreedRate; // same '' -> undefined, comma-tolerant parse
  const method = values.pricing_method;
  if (method === 'per_km') {
    return {
      pricing_method: method,
      distance_km_one_way: num(values.distance_km_one_way),
      is_round_trip: values.is_round_trip,
      price_per_km: num(values.price_per_km),
    };
  }
  if (method === 'flat') {
    return { pricing_method: method, flat_price_eur: num(values.flat_price_eur) };
  }
  if (method === 'hourly') {
    return {
      pricing_method: method,
      hourly_rate_eur: num(values.hourly_rate_eur),
      billed_hours: num(values.billed_hours),
    };
  }
  if (method === 'ton_km') {
    return {
      pricing_method: method,
      tonkm_capacity_tons: num(values.tonkm_capacity_tons),
      distance_km_one_way: num(values.distance_km_one_way),
    };
  }
  return {};
}

export function useOrderForm({ onCreated } = {}) {
  const t = useT();
  const [values, setValues] = useState(EMPTY);
  const [validationError, setValidationError] = useState('');
  const createOrder = useCreateOrder();

  const set = (field) => (event) => setValues((v) => ({ ...v, [field]: event.target.value }));
  // The same write, without an event to unwrap. The recommended-driver list
  // is a list of buttons, not an <input>, so it has no event.target.value to
  // hand back — and routing it through a synthetic event just to reuse set()
  // would be a fake event built only to be taken apart again.
  const setValue = (field, value) => setValues((v) => ({ ...v, [field]: value }));
  // V-289. Also clears validationError and the mutation's own error state
  // (React Query's reset()), not just `values` — QuickDispatchModal and
  // CreateOrderDrawer both render unconditionally in their parent view
  // (Modal/Drawer only hide their OWN content when closed, per Overlay.jsx's
  // `if (!open) return null`), so this hook's state never unmounts across
  // an open/close cycle on its own. Exposed for both host components to
  // call on every dismissal — Cancel, ✕, Escape, backdrop click — not only
  // on a successful create, which is the only path that already called it.
  const reset = () => {
    setValues(EMPTY);
    setValidationError('');
    createOrder.reset();
  };

  const usingNewSite = values.location_mode === 'new';

  const submit = () => {
    const weight = Number.parseFloat(values.estimated_weight_tons);
    // parseInt, ne Number: '2.5' turi buti atmestas cia, o ne serveryje —
    // puse reiso nera nei krovinys, nei masina. NaN krenta i validacija
    // zemiau kartu su tusciu lauku.
    const loads = Number.parseInt(values.loads, 10);
    const hasLocation = usingNewSite
      ? Boolean(values.new_site_name.trim() && values.new_site_pin)
      : Boolean(values.site_id);
    if (!values.quarry_id || !hasLocation || !values.cargo_type.trim() || Number.isNaN(weight)
        || !Number.isFinite(loads) || loads < 1 || String(values.loads).includes('.')) {
      setValidationError(t('orders.fillAllFields'));
      return;
    }
    setValidationError('');
    createOrder.mutate(
      {
        driver_id: values.driver_id || undefined,
        quarry_id: values.quarry_id,
        ...(usingNewSite
          ? {
              site: {
                site_name: values.new_site_name.trim(),
                client_name: values.new_client_name.trim() || undefined,
                lat: values.new_site_pin.lat,
                lng: values.new_site_pin.lng,
                geofence_radius_m: Number(values.new_site_radius_m) || 150,
              },
            }
          : { site_id: values.site_id }),
        cargo_type: values.cargo_type.trim(),
        estimated_weight_tons: weight,
        // Absent when it is 1 — vienas reisas nera rezervavimas su vienu
        // reisu, ir marsrutas be sio lauko elgiasi lygiai kaip elgesi.
        ...(loads > 1 ? { loads } : {}),
        // Nesiunciam tuscios: '' nera data, o marsrutas tuscia eilute ir taip
        // laikytu "dabar" — tyleti aiskiau nei siusti nieko nereiskianti lauka.
        ...(values.scheduled_for ? { scheduled_for: values.scheduled_for } : {}),
        // Absent when the field is blank, which means the tenant's price
        // list decides — the ordinary case, and the one every load created
        // before V-35 is.
        agreed_price_per_ton: agreedRate(values.agreed_price_per_ton),
        // Absent rather than empty-string when no hours were promised. The
        // day rides on scheduled_for just above; the server refuses a window
        // with no day (blueprints/orders.py::_validated_scheduled_for), which
        // is why an empty pair must not be sent as ''.
        ...deliveryWindowFields(values),
        note: values.note.trim() || undefined,
        ...pricingFields(values),
      },
      {
        onSuccess: (created) => {
          reset();
          // Antras argumentas yra VISAS marsruto atsakymas — jame keliauja
          // created_order_ids ir batch_id, kuriu `order` savyje neturi.
          // Pirmas lieka nepakites, kad kiekvienas esamas kvietejas skaitytu
          // ji lygiai taip, kaip skaite.
          onCreated?.(created?.order ?? created, created);
        },
      },
    );
  };

  const errorText =
    validationError || (createOrder.isError ? `${t('common.error')}: ${createOrder.error.message}` : '');

  return { values, set, setValue, submit, reset, errorText, isPending: createOrder.isPending };
}
